import React, { useEffect, useMemo, useState } from 'react';
import { UserCheck, Loader2, Link2, Wand2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioUnidadeGeradora } from '../../../types';
import { listarUcsParaFilaVinculo, vincularUcsAUg, UcFilaVinculo } from './ucsAprovadasPendentesActions';

interface UcsAprovadasPendentesCardProps {
  ug: RateioUnidadeGeradora;
  onVinculado: () => void;
}

const SEM_SITUACAO = '__sem_situacao__';
const SITUACAO_OPTIONS: { value: string; label: string }[] = [
  { value: 'Ativa', label: 'Ativa' },
  { value: 'Ativa-S/-Rateio', label: 'Ativa-S/-Rateio' },
  { value: '1° Ciclo', label: '1° Ciclo' },
  { value: '2° Ciclo', label: '2° Ciclo' },
  { value: 'Reativacao', label: 'Reativação' },
  { value: 'Pausada', label: 'Pausada' },
  { value: 'Cancelada', label: 'Cancelada' },
  { value: SEM_SITUACAO, label: '(Sem situação)' },
];

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Elegível pra ser efetivamente vinculada: cadastro aprovado e não Cancelada. */
const elegivelVinculo = (uc: UcFilaVinculo) => uc.cadastro_aprovado === true && uc.situacao !== 'Cancelada';

/**
 * Aproximação gulosa (não é subset-sum exato): ordena as UCs por consumo
 * médio decrescente e vai empilhando enquanto não ultrapassar o alvo, pra
 * chegar o mais perto possível "por baixo" sem estourar a % pedida.
 */
const selecionarUcsPorAlvo = (ucs: UcFilaVinculo[], alvoKwh: number): Set<number> => {
  const ordenadas = [...ucs].sort((a, b) => (b.consumo_medio_kwh || 0) - (a.consumo_medio_kwh || 0));
  const selecionadas = new Set<number>();
  let soma = 0;
  for (const uc of ordenadas) {
    const consumo = uc.consumo_medio_kwh || 0;
    if (consumo <= 0) continue;
    if (soma + consumo <= alvoKwh || selecionadas.size === 0) {
      selecionadas.add(uc.id);
      soma += consumo;
    }
    if (soma >= alvoKwh) break;
  }
  return selecionadas;
};

/**
 * Fila de vínculo com UG. Mostra qualquer UC sem UG (inclusive Canceladas ou
 * com cadastro pendente), exceto quando situação em branco/nula E cadastro
 * não aprovado ao mesmo tempo (ver ucsAprovadasPendentesActions.ts). Só é
 * possível marcar/vincular UCs elegíveis (cadastro aprovado e não Cancelada)
 * — as demais aparecem só pra contexto, com o checkbox desabilitado.
 */
const UcsAprovadasPendentesCard: React.FC<UcsAprovadasPendentesCardProps> = ({ ug, onVinculado }) => {
  const { addToast } = useToast();
  const [pendentes, setPendentes] = useState<UcFilaVinculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [vinculando, setVinculando] = useState(false);
  const [percentualAlvo, setPercentualAlvo] = useState('');
  const [filtroSituacoes, setFiltroSituacoes] = useState<Set<string>>(
    () => new Set(SITUACAO_OPTIONS.map(o => o.value))
  );
  const [filtroCadastro, setFiltroCadastro] = useState<'todos' | 'aprovado' | 'pendente'>('todos');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await listarUcsParaFilaVinculo();
      setPendentes(data);
      setSelecionadas(prev => new Set(Array.from(prev).filter(id => data.some(uc => uc.id === id))));
    } catch (error) {
      console.error('Error loading UCs para fila de vínculo', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSituacaoFiltro = (value: string) => {
    setFiltroSituacoes(prev => {
      const nova = new Set(prev);
      if (nova.has(value)) nova.delete(value);
      else nova.add(value);
      return nova;
    });
  };

  const pendentesFiltrados = useMemo(
    () =>
      pendentes.filter(uc => {
        const situacaoKey = uc.situacao || SEM_SITUACAO;
        if (!filtroSituacoes.has(situacaoKey)) return false;
        if (filtroCadastro === 'aprovado' && !uc.cadastro_aprovado) return false;
        if (filtroCadastro === 'pendente' && uc.cadastro_aprovado) return false;
        return true;
      }),
    [pendentes, filtroSituacoes, filtroCadastro]
  );

  const elegiveisFiltrados = useMemo(() => pendentesFiltrados.filter(elegivelVinculo), [pendentesFiltrados]);

  const toggleSelecionada = (uc: UcFilaVinculo) => {
    if (!elegivelVinculo(uc)) return;
    setSelecionadas(prev => {
      const nova = new Set(prev);
      if (nova.has(uc.id)) nova.delete(uc.id);
      else nova.add(uc.id);
      return nova;
    });
  };

  const todasElegiveisMarcadas =
    elegiveisFiltrados.length > 0 && elegiveisFiltrados.every(uc => selecionadas.has(uc.id));

  const toggleTodas = () => {
    const ids = elegiveisFiltrados.map(uc => uc.id);
    setSelecionadas(prev => {
      const nova = new Set(prev);
      if (todasElegiveisMarcadas) ids.forEach(id => nova.delete(id));
      else ids.forEach(id => nova.add(id));
      return nova;
    });
  };

  const capacidadeKwp = ug.capacidade_kwp || 0;
  const pct = parseFloat(percentualAlvo.replace(',', '.')) || 0;
  const alvoKwh = capacidadeKwp * (pct / 100);
  const somaSelecionadaKwh = pendentes
    .filter(uc => selecionadas.has(uc.id))
    .reduce((s, uc) => s + (uc.consumo_medio_kwh || 0), 0);

  const handleSelecionarAutomatico = () => {
    if (!capacidadeKwp || alvoKwh <= 0) return;
    setSelecionadas(selecionarUcsPorAlvo(elegiveisFiltrados, alvoKwh));
  };

  // Linhas marcadas sobem e ficam agrupadas no topo, pra facilitar conferir a
  // seleção (manual ou automática) sem precisar rolar a tabela toda.
  const pendentesOrdenados = useMemo(() => {
    const selecionadasList = pendentesFiltrados.filter(uc => selecionadas.has(uc.id));
    const naoSelecionadas = pendentesFiltrados.filter(uc => !selecionadas.has(uc.id));
    return [...selecionadasList, ...naoSelecionadas];
  }, [pendentesFiltrados, selecionadas]);

  const handleVincular = async () => {
    if (selecionadas.size === 0) return;
    setVinculando(true);
    try {
      await vincularUcsAUg(ug.id, Array.from(selecionadas));
      addToast(`${selecionadas.size} UC(s) vinculada(s) a ${ug.nome}.`, 'success');
      setSelecionadas(new Set());
      await carregar();
      onVinculado();
    } catch (error) {
      console.error('Error linking pending UCs', error);
      addToast('Erro ao vincular UCs.', 'error');
    } finally {
      setVinculando(false);
    }
  };

  if (loading || pendentes.length === 0) return null;

  return (
    <div className="mb-6 bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-emerald-100 dark:border-emerald-900/30 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-300 flex items-center gap-1.5">
            <UserCheck size={14} /> Fila de Vínculo com UG ({pendentesFiltrados.length}
            {pendentesFiltrados.length !== pendentes.length ? ` de ${pendentes.length}` : ''})
          </h3>
          <p className="text-xs text-emerald-700/70 dark:text-emerald-400/70 mt-0.5">
            Só UCs com cadastro aprovado e situação diferente de Cancelada podem ser vinculadas — as demais aparecem
            só pra contexto. Selecione as que pertencem a {ug.nome} e confirme.
          </p>
        </div>
        <button
          type="button"
          disabled={selecionadas.size === 0 || vinculando}
          onClick={handleVincular}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {vinculando ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
          Vincular {selecionadas.size > 0 ? `(${selecionadas.size})` : ''} a {ug.nome}
        </button>
      </div>

      <div className="px-4 py-3 border-b border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/70 dark:bg-emerald-900/15 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-emerald-800/80 dark:text-emerald-400/80">Situação:</span>
          {SITUACAO_OPTIONS.map(opt => {
            const active = filtroSituacoes.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleSituacaoFiltro(opt.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-emerald-800/80 dark:text-emerald-400/80">Cadastro:</span>
          <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
            {(['todos', 'aprovado', 'pendente'] as const).map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setFiltroCadastro(v)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  filtroCadastro === v
                    ? 'bg-white dark:bg-gray-800 text-primary shadow'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
              >
                {v === 'todos' ? 'Todos' : v === 'aprovado' ? 'Aprovado' : 'Pendente'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/70 dark:bg-emerald-900/15">
        {!capacidadeKwp ? (
          <p className="text-xs text-emerald-700/70 dark:text-emerald-400/70">
            {ug.nome} não tem capacidade instalada (kWp) cadastrada — não é possível selecionar automaticamente por %.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-emerald-800/80 dark:text-emerald-400/80 mb-1">
                Selecionar automaticamente por % da capacidade da UG ({formatKwh(capacidadeKwp)} kWp)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={percentualAlvo}
                  onChange={e => setPercentualAlvo(e.target.value)}
                  placeholder="Ex: 10"
                  className="w-24 bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-900/40 rounded-lg px-3 py-1.5 text-sm"
                />
                <span className="text-sm text-emerald-800/70 dark:text-emerald-400/70">%</span>
                <button
                  type="button"
                  disabled={pct <= 0}
                  onClick={handleSelecionarAutomatico}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Wand2 size={14} /> Selecionar automaticamente
                </button>
              </div>
            </div>
            {pct > 0 && (
              <p className="text-xs text-emerald-700/70 dark:text-emerald-400/70">
                Alvo: {formatKwh(alvoKwh)} kWh ({pct.toFixed(1)}%) — Selecionado: {formatKwh(somaSelecionadaKwh)} kWh (
                {selecionadas.size} UC{selecionadas.size !== 1 ? 's' : ''})
              </p>
            )}
          </div>
        )}
      </div>

      {pendentesFiltrados.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
          Nenhuma UC corresponde aos filtros selecionados.
        </p>
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-emerald-800/60 dark:text-emerald-400/60 uppercase whitespace-nowrap">
                <th className="py-2 px-4">
                  <input
                    type="checkbox"
                    checked={todasElegiveisMarcadas}
                    onChange={toggleTodas}
                    disabled={elegiveisFiltrados.length === 0}
                    className="cursor-pointer disabled:cursor-not-allowed"
                  />
                </th>
                <th className="py-2 px-4">UC</th>
                <th className="py-2 px-4">Outro Nº UC</th>
                <th className="py-2 px-4">Titular</th>
                <th className="py-2 px-4">CPF/CNPJ</th>
                <th className="py-2 px-4">Situação</th>
                <th className="py-2 px-4">Status Financeiro</th>
                <th className="py-2 px-4">Cidade/UF</th>
                <th className="py-2 px-4">Consumo Médio</th>
                <th className="py-2 px-4">Grupo Tarifário</th>
                <th className="py-2 px-4">Concessionária</th>
                <th className="py-2 px-4">Ligação</th>
                <th className="py-2 px-4">Medidor</th>
                <th className="py-2 px-4">GD</th>
                <th className="py-2 px-4">Cadastro</th>
              </tr>
            </thead>
            <tbody>
              {pendentesOrdenados.map((uc, idx) => {
                const marcada = selecionadas.has(uc.id);
                const elegivel = elegivelVinculo(uc);
                const inicioNaoSelecionadas =
                  idx > 0 && marcada === false && selecionadas.has(pendentesOrdenados[idx - 1].id);
                return (
                  <React.Fragment key={uc.id}>
                    {inicioNaoSelecionadas && (
                      <tr>
                        <td colSpan={15} className="p-0">
                          <div className="border-t-2 border-emerald-300 dark:border-emerald-700" />
                        </td>
                      </tr>
                    )}
                    <tr
                      className={`border-t border-emerald-100/70 dark:border-emerald-900/20 whitespace-nowrap ${
                        marcada ? 'bg-emerald-100/50 dark:bg-emerald-900/20' : ''
                      } ${!elegivel ? 'opacity-60' : ''}`}
                    >
                      <td className="py-2 px-4">
                        <input
                          type="checkbox"
                          checked={marcada}
                          disabled={!elegivel}
                          onChange={() => toggleSelecionada(uc)}
                          title={!elegivel ? 'Não elegível: cadastro pendente ou situação Cancelada' : undefined}
                          className="cursor-pointer disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="py-2 px-4 font-medium text-gray-900 dark:text-gray-100">{uc.numero_uc}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.outro_numero_uc || '—'}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                        {uc.nome_cliente || uc.titular_fatura || '—'}
                      </td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                        {uc.cpf_cnpj_titular || uc.cpf_cnpj_cliente || '—'}
                      </td>
                      <td className="py-2 px-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {uc.situacao || '(Sem situação)'}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        {uc.status_financeiro ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              uc.status_financeiro === 'Inadimplente'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {uc.status_financeiro}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                        {[uc.cidade, uc.estado].filter(Boolean).join('/') || '—'}
                      </td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                        {uc.consumo_medio_kwh != null ? `${uc.consumo_medio_kwh} kWh` : '—'}
                      </td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.grupo_tarifario || '—'}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.concessionaria || '—'}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.tipo_ligacao || '—'}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.num_medidor || '—'}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{uc.modalidade_gd || '—'}</td>
                      <td className="py-2 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            uc.cadastro_aprovado
                              ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          }`}
                        >
                          {uc.cadastro_aprovado ? 'Aprovada' : 'Pendente'}
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UcsAprovadasPendentesCard;
